<template>
  <el-form ref="formRef" :model="innerForm" :rules="rules" label-position="top" class="raise-form">
    <div class="section-card">
      <el-form-item label="选择本项目下的素材" prop="materialType">
        <el-radio-group v-model="innerForm.materialType" @change="onMaterialTypeChange">
          <el-radio-button ghost :label="TOUTIAO_RAISE_MATERIAL_TYPE.ALL">全部素材</el-radio-button>
          <el-radio-button ghost :label="TOUTIAO_RAISE_MATERIAL_TYPE.SPECIFIED">指定素材</el-radio-button>
        </el-radio-group>

        <template v-if="innerForm.materialType === TOUTIAO_RAISE_MATERIAL_TYPE.SPECIFIED">
          <div class="material-search-box">
            <el-input
              v-model="searchText"
              placeholder="请输入素材名称或ID进行筛选"
              clearable
              prefix-icon="el-icon-search"
              style="width: 300px"
            />
          </div>

          <TableSelectionPanel
            ref="tablePanelRef"
            :loading="loading"
            :table-data="filteredTableData"
            :result="innerForm.materialIds"
            @table-row-select="onTableRowSelect"
            @table-select-all="onTableSelectAll"
            @clear-all="clearAll"
            @close-item="onResItemRemove"
          >
            <el-table-column label="素材名称">
              <template #header>
                <el-tabs class="material-type-tabs" v-model="activeMaterialTypeTab" @tab-click="onTabChange">
                  <ElTabPane name="video" label="视频素材"></ElTabPane>
                  <ElTabPane name="playable" label="试玩素材"></ElTabPane>
                </el-tabs>
              </template>

              <template slot-scope="{ row }">
                <div class="material-box">
                  <div class="image-preview">
                    <el-image :src="row.videoCoverUrl" style="height: 60px" fit="contain"></el-image>
                  </div>
                  <div>
                    <p>{{ row.name }}</p>
                    <p>ID:{{ row.id }}</p>
                  </div>
                </div>
              </template>
            </el-table-column>
          </TableSelectionPanel>
        </template>
      </el-form-item>

      <el-form-item label="起量预算" prop="raiseBudget" required>
        <el-input v-model.number="innerForm.raiseBudget" type="number" placeholder="请输入起量预算">
          <template slot="append">元</template>
        </el-input>
      </el-form-item>

      <el-form-item label="生效时间" prop="effectiveType" required>
        <el-radio-group v-model="innerForm.effectiveType">
          <el-radio-button ghost label="immediate">立即生效</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <el-form-item label="结束时间" prop="endTime" required>
        <el-date-picker
          v-model="innerForm.endTime"
          type="datetime"
          value-format="yyyy-MM-dd HH:mm"
          format="yyyy-MM-dd HH:mm"
          default-time="12:00:00"
          :picker-options="dateTimePickerOpts"
          placeholder="请选择起量结束时间"
          style="width: 100%"
        />
      </el-form-item>
    </div>
  </el-form>
</template>

<script setup>
import { ref, reactive, watch, defineProps, defineExpose, computed } from 'vue'
import moment from 'moment'
import { TOUTIAO_RAISE_MATERIAL_TYPE } from '../../detail.data'
import TableSelectionPanel from '@/components/table-selection-panel/index.vue'
import { useTableSelectionPanel } from '@/components/table-selection-panel/hooks/useSelection'
import { getPtomPlanToutiaoRaiseMaterialInfo } from '@/api/ptom.data.model'

const getDefaultForm = () => ({
  materialType: TOUTIAO_RAISE_MATERIAL_TYPE.ALL,
  raiseBudget: undefined,
  materialIds: [],
  effectiveType: 'immediate',
  effectiveAt: '',
  endTime: ''
})

const dateTimePickerOpts = {
  disabledDate: (time) => time.getTime() < Date.now() - 24 * 60 * 60 * 1000
}

const props = defineProps({
  planId: {
    type: Number,
    default: 0
  }
})

const formRef = ref(null)
const innerForm = reactive(getDefaultForm())

const materialInfo = ref(null)
const videoTableData = ref([])
const playableTableData = ref([])
const loading = ref(false)
const searchText = ref('')
const activeMaterialTypeTab = ref('video')

const { tablePanelRef, restoreSelection, onTableRowSelect, onTableSelectAll, onResItemRemove, clearAll } = useTableSelectionPanel({
  result: computed({
    get() {
      return innerForm.materialIds
    },
    set(v) {
      innerForm.materialIds = v
    }
  }),
  tableData: computed(() => filteredTableData.value)
})

const currentTableData = computed(() => {
  return activeMaterialTypeTab.value === 'playable' ? playableTableData.value : videoTableData.value
})

const filteredTableData = computed(() => {
  const source = currentTableData.value
  if (!searchText.value.trim()) {
    return source
  }
  const searchLower = searchText.value.trim().toLowerCase()
  return source.filter((item) => {
    const nameMatch = item.name && item.name.toLowerCase().includes(searchLower)
    const idMatch = String(item.id).includes(searchLower)
    return nameMatch || idMatch
  })
})

const getInfo = (val) => {
  if (val === TOUTIAO_RAISE_MATERIAL_TYPE.ALL) return

  loading.value = true
  getPtomPlanToutiaoRaiseMaterialInfo({ planId: props.planId })
    .then((data) => {
      materialInfo.value = data

      videoTableData.value = data.videoMaterials.map((i) => ({ ...i, name: i.videoName, id: i.materialId }))
      playableTableData.value = data.trialPlayMaterials.map((i) => ({ ...i, name: i.videoName, id: i.materialId }))
    })
    .finally(() => {
      loading.value = false
    })
}

const onMaterialTypeChange = () => {
  innerForm.materialIds = []
  activeMaterialTypeTab.value = 'video'
  searchText.value = ''
  getInfo()
}

const onTabChange = () => {
  searchText.value = ''
  innerForm.materialIds = []
  clearAll()
}

const validateBudget = (value, callback) => {
  if (value === undefined || value === null || value === '') {
    return callback(new Error('请输入起量预算'))
  }
  if (Number(value) < 100 || Number(value) > 99999999.99) {
    return callback(new Error('起量预算不可低于100元，不可高于99999999.99元'))
  }
  callback()
}

const parseEndTime = (value) => {
  if (!value) return NaN
  if (typeof value === 'number') return value
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'string') {
    const ts = moment(value, 'YYYY-MM-DD HH:mm', true).valueOf()
    return ts
  }
  return NaN
}

const rules = {
  materialType: [
    {
      required: true,
      validator(rule, value, callback) {
        if (value === TOUTIAO_RAISE_MATERIAL_TYPE.SPECIFIED) {
          if (!innerForm.materialIds.length) {
            return callback(new Error('请选择起量素材'))
          }
        }

        callback()
      },
      trigger: 'change'
    }
  ],
  raiseBudget: [
    {
      validator: (_rule, value, callback) => {
        validateBudget(value, callback)
      },
      trigger: ['blur', 'change']
    }
  ],
  endTime: [
    { required: true, message: '请选择结束时间', trigger: 'change' },
    {
      validator: (_rule, value, callback) => {
        const endTs = parseEndTime(value)
        if (isNaN(endTs)) {
          return callback(new Error('结束时间格式不正确'))
        }
        const nowTs = Date.now()
        if (endTs < nowTs) {
          return callback(new Error('结束时间不可早于当前时间'))
        }
        if (endTs - nowTs < 30 * 60 * 1000) {
          return callback(new Error('方案持续时间不能小于30分钟'))
        }
        callback()
      },
      trigger: 'change'
    }
  ]
}

const validate = () => {
  return new Promise((resolve) => {
    if (!formRef.value) {
      resolve(false)
      return
    }
    formRef.value.validate((valid) => {
      resolve(valid)
    })
  })
}

defineExpose({
  validate,
  getForm() {
    return innerForm
  }
})
</script>

<style scoped lang="scss">
.section-card {
  padding: 16px;
  border-radius: 4px;

  .material-search-box {
    margin-top: 10px;
  }

  ::v-deep .material-type-tabs {
    .el-tabs__header {
      margin: 0;
    }
  }
}

.tip-text {
  margin-top: 6px;
  font-size: 12px;
}

.material-box {
  display: flex;
  align-items: flex-end;

  .image-preview {
    min-width: 120px;
    text-align: center;
  }
}
</style>
